/**
 * scriptSureMessage.ts
 *
 * The one signal ScriptSure's embedded screens give the page hosting them, and
 * the rules for when to act on it. Kept apart from ScriptSureWindow so the
 * check can be tested without mounting a modal.
 */
import type { DawWindowKind } from '@/services/prescriptions.service';

/**
 * ScriptSure's embedded UI posts this bare string to the parent window when the
 * prescriber finishes or closes the prescribing workflow. It is the only signal
 * the embed gives us. Which other screens post it is still an open question
 * for DAW (plan U8).
 */
export const SCRIPTSURE_CLOSE_SIGNAL = '200';

/**
 * The only origins whose '200' we act on — ScriptSure's staging and production
 * UI hosts, matching config/integrations.php daw.urls.*.ui. ANY page on the
 * internet can postMessage us that string, so this list is the first of two
 * gates; the second is that the message came from the window's own iframe.
 * Deliberately a hardcoded allowlist rather than one derived from the
 * server-supplied iframe URL: a bad value there must not be able to widen it.
 */
export const SCRIPTSURE_ORIGINS: readonly string[] = [
  'https://ssu.scriptsure.com',
  'https://app.scriptsure.com',
];

/**
 * True only for ScriptSure's '200', from an allowed origin, sent by the frame
 * the window is showing.
 */
export function isScriptSureCloseSignal(
  event: Pick<MessageEvent, 'origin' | 'source' | 'data'>,
  frameWindow: Window | null | undefined,
): boolean {
  if (!SCRIPTSURE_ORIGINS.includes(event.origin)) return false;
  // An allowed ORIGIN is not enough: any other frame, popup or opener served
  // from that host could post the same string. The signal only counts when it
  // came from the iframe this window is showing.
  if (!frameWindow || event.source !== frameWindow) return false;
  return event.data === SCRIPTSURE_CLOSE_SIGNAL;
}

/**
 * Whether the signal closes a window of this kind. Only the prescribing chart:
 * there the signal means the prescription is done. A Messages, history, Reports
 * or full-application window may be mid-way through something the signal says
 * nothing about (a renewal being answered, a controlled-substance sign-up), so
 * those windows stay open and only the activity list refreshes.
 */
export function closesOnSignal(kind: DawWindowKind): boolean {
  return kind === 'chart';
}
